<template>
  <div class="container-fluid py-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h4 class="text-burgundy fw-bold mb-0">
        {{ $t(isEmployeeMode ? "attendance.myTitle" : "attendance.adminTitle") }}

      </h4>

      <!-- ✅ Admin toggle (same route, query param) -->
      <div v-if="isAdmin" class="d-flex gap-2">
        <button
          v-if="!isEmployeeMode"
          class="btn btn-outline-secondary"
          @click="goMyAttendance"
        >
          <i class="fas fa-user-check me-2"></i>
          {{ $t("attendance.myAttendance") }}

        </button>

        <button
          v-else
          class="btn btn-outline-dark"
          @click="goAdminAttendance"
        >
          <i class="fas fa-arrow-left me-2"></i>
          {{ $t("attendance.backToAdmin") }}

        </button>
      </div>
    </div>

    <!-- ERROR -->
    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <!-- LOADING -->
    <div v-else-if="loading" class="text-center">
      <i class="fas fa-spinner fa-spin me-2"></i> {{ $t("attendance.loading") }}

    </div>

    <!-- ================= EMPLOYEE MODE (even admin) ================= -->
    <div v-else-if="isEmployeeMode">
      <!-- ACTION CARD -->
      <div class="card shadow-sm p-4 text-center">
        <h5 class="fw-bold mb-3">{{ $t("attendance.today") }} : {{ today }}
</h5>

        <!-- NOT CHECKED IN -->
        <div v-if="!todayRecord || (!todayRecord.checkin && !todayRecord.checkout)">

  <!-- STEP 1: Check In button -->
  <div v-if="!selectingMode">
    <button
      class="btn btn-burgundy"
      :disabled="busy"
      @click="selectingMode = true"
    >
      {{ $t("attendance.checkIn") }}

    </button>
  </div>

  <!-- STEP 2: Select work mode -->
  <div v-else class="d-flex justify-content-center gap-3">
    <button
      class="btn btn-outline-primary"
      :disabled="busy"
      @click="confirmCheckIn('field')"
    >
      🌍 {{ $t("attendance.remote") }}

    </button>

    <button
      class="btn btn-outline-success"
      :disabled="busy"
      @click="confirmCheckIn('onsite')"
    >
      🏢 {{ $t("attendance.onsite") }}

    </button>
  </div>

</div>


        <!-- CHECKED IN -->
        <div v-else-if="todayRecord.checkin && !todayRecord.checkout">
          <p class="text-success fw-semibold">
            ✔ {{ $t("attendance.checkedInAt") }} {{ formatTime(todayRecord.checkin.time) }}

          </p>

          <button class="btn btn-warning mt-2" :disabled="busy" @click="checkOut">
           {{ $t("attendance.checkOut") }}

          </button>
        </div>

        <!-- COMPLETED -->
        <div v-else>
          <p class="text-success">✔ Checked in</p>
          <p class="text-danger">✔ Checked out</p>
          <span class="badge bg-burgundy mt-2">{{ $t("attendance.dayCompleted") }}
</span>
        </div>

        <div class="small text-muted mt-3">
          * {{ $t("attendance.locationInfo") }}

        </div>
      </div>

      <!-- HISTORY -->
      <div class="mt-4">
        <h5 class="fw-bold text-burgundy mb-3">{{ $t("attendance.myHistory") }}
</h5>

        <div v-if="orderedDates.length === 0" class="alert alert-info">
          {{ $t("attendance.noHistory") }}

        </div>

        <div v-else class="table-responsive shadow-sm">
          <table class="table align-middle text-center">
            <thead class="bg-burgundy text-white">
              <tr>
                <th>{{ $t("attendance.table.date") }}</th>
                <th>{{ $t("attendance.table.checkIn") }}</th>
                <th>{{ $t("attendance.table.checkOut") }}</th>
                <th>{{ $t("attendance.table.map") }}</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="date in orderedDates" :key="date">
                <td class="fw-bold">{{ date }}</td>

                <td>
                  {{
                    grouped[date]?.[0]?.checkin
                      ? formatDateTime(grouped[date][0].checkin.time)
                      : "-"
                  }}
                </td>

                <td>
                  {{
                    grouped[date]?.[0]?.checkout
                      ? formatDateTime(grouped[date][0].checkout.time)
                      : "-"
                  }}
                </td>

                <td>
                  <a
                    v-if="makeMapLink(grouped[date]?.[0]?.checkin)"
                    class="btn btn-sm btn-outline-secondary me-2"
                    :href="makeMapLink(grouped[date][0].checkin)"
                    target="_blank"
                  >
                    Check-in map
                  </a>

                  <a
                    v-if="makeMapLink(grouped[date]?.[0]?.checkout)"
                    class="btn btn-sm btn-outline-secondary"
                    :href="makeMapLink(grouped[date][0].checkout)"
                    target="_blank"
                  >
                    Check-out map
                  </a>

                  <span
                    v-if="!makeMapLink(grouped[date]?.[0]?.checkin) && !makeMapLink(grouped[date]?.[0]?.checkout)"
                  >
                    -
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ================= ADMIN MODE ================= -->
    <div v-else>
      <div v-if="orderedDates.length === 0" class="alert alert-info">
        No attendance data yet.
      </div>

      <div v-else v-for="date in orderedDates" :key="date" class="mb-4">
        <h5 class="fw-bold text-burgundy">{{ date }}</h5>

        <div class="table-responsive shadow-sm">
          <table class="table align-middle text-center">
            <thead class="bg-burgundy text-white">
              <tr>
                <th>{{ $t("attendance.table.user") }}</th>

                <th>{{ $t("attendance.table.checkIn") }}</th>

                <th>Check Out</th>

                <th>{{ $t("attendance.table.status") }}</th>

                <th>{{ $t("attendance.table.map") }}</th>

              </tr>
            </thead>

            <tbody>
              <tr v-for="rec in grouped[date]" :key="rec.user?.id">
                <td class="fw-bold">{{ rec.user?.name }}</td>

                <td>{{ rec.checkin ? formatDateTime(rec.checkin.time) : "-" }}</td>
                <td>{{ rec.checkout ? formatDateTime(rec.checkout.time) : "-" }}</td>

                <td>
                  <span v-if="rec.checkout" class="badge bg-danger">OUT</span>
                  <span v-else-if="rec.checkin" class="badge bg-success">IN</span>
                  <span v-else class="badge bg-secondary">-</span>
                </td>

                <td>
                  <template v-if="makeMapLink(rec.checkin) || makeMapLink(rec.checkout)">
                    <a
                      v-if="makeMapLink(rec.checkin)"
                      class="btn btn-sm btn-outline-secondary me-2"
                      :href="makeMapLink(rec.checkin)"
                      target="_blank"
                    >
                      In map
                    </a>

                    <a
                      v-if="makeMapLink(rec.checkout)"
                      class="btn btn-sm btn-outline-secondary"
                      :href="makeMapLink(rec.checkout)"
                      target="_blank"
                    >
                      Out map
                    </a>
                  </template>

                  <template v-else>
                    -
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="small text-muted mt-2">
         
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "@/api";

// ROUTER
const route = useRoute();
const router = useRouter();

// STATE
const loading = ref(false);
const busy = ref(false);
const error = ref(null);
const grouped = ref({});
const selectingMode = ref(false);
const selectedWorkMode = ref(null);


// USER
const rawUser = localStorage.getItem("user");
const user = rawUser ? JSON.parse(rawUser) : {};

// ✅ ROLE (robuste)
const isAdmin = computed(() => {
  const u = user || {};
  if (u.role === "admin") return true;

  const roles = u.roles;
  if (Array.isArray(roles)) {
    return roles.some(r => (typeof r === "string" ? r === "admin" || r === "SuperAdmin" : r?.name === "admin" || r?.name === "SuperAdmin"));
  }
  if (typeof roles === "string") return roles === "admin" || roles === "SuperAdmin";
  return false;
});


const forcedEmployee = computed(() => String(route.query.view || "").toLowerCase() === "me");


const isEmployeeMode = computed(() => forcedEmployee.value || !isAdmin.value);

const pageTitle = computed(() =>  isEmployeeMode.value    ? $t("attendance.myTitle")    : $t("attendance.adminTitle")
);



function getLocalISODate() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
const today = getLocalISODate();

// Dates triées
const orderedDates = computed(() =>
  Object.keys(grouped.value || {}).sort((a, b) => b.localeCompare(a))
);

// Today record (employee) : 1 record/jour
const todayRecord = computed(() => {
  const list = grouped.value?.[today] || [];
  return list[0] || null;
});

// helpers
function formatDateTime(dt) {
  if (!dt) return "-";
  return new Date(dt).toLocaleString();
}
function formatTime(dt) {
  if (!dt) return "-";
  return new Date(dt).toLocaleTimeString();
}

/**
 * ✅ Consomme la map même si backend renvoie map_url = null
 * Ordre :
 * 1) map_url
 * 2) lat/lng
 * 3) address
 */
function makeMapLink(point) {
  if (!point) return null;

  if (point.map_url) return point.map_url;

  if (point.lat != null && point.lng != null) {
    return `https://www.google.com/maps/search/?api=1&query=${point.lat},${point.lng}`;
  }

  if (point.address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(point.address)}`;
  }

  return null;
}


function getLocationPayloadStrict() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      return reject(new Error("Geolocation not supported on this browser."));
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          work_mode: "onsite", // ✅ OBLIGATOIRE (BACKEND)
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          source: "browser",
          reverse_geocode: true,
          device_id: localStorage.getItem("device_id") || null,
        });
      },
      () => {
        reject(
          new Error(
            "Location permission is required to check-in/out. Please allow location in your browser."
          )
        );
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  });
}


// API
async function fetchAdminAttendance() {
  loading.value = true;
  error.value = null;
  try {
    const { data } = await axios.get("employee/attendance");
    grouped.value = data.grouped || {};
  } catch (e) {
    error.value = e?.response?.data?.message || "Failed to load attendance";
  } finally {
    loading.value = false;
  }
}

async function fetchEmployeeAttendance() {
  loading.value = true;
  error.value = null;

  try {
    // ⚠️ route back = myHistroy (typo)
    const { data } = await axios.get("employee/attendance/users/myHistroy");

    const map = {};
    (data.data || []).forEach(d => {
      map[d.date] = [{
        user: d.user,
        checkin: d.checkin,
        checkout: d.checkout
      }];
    });

    grouped.value = map;

  } catch (e) {
    const status = e?.response?.status;
    const msg = e?.response?.data?.message;

    // ✅ No attendance => vide, pas une erreur
    if (status === 404 && String(msg || "").toLowerCase().includes("no attendance data")) {
      grouped.value = {};
      error.value = null;
    } else {
      error.value = msg || "Failed to load attendance";
    }

  } finally {
    loading.value = false;
  }
}

async function checkIn() {
  busy.value = true;
  error.value = null;
  try {
    const payload = await getLocationPayloadStrict();
    await axios.post("employee/attendance/check-in", payload);
    await fetchEmployeeAttendance();
  } catch (e) {
    error.value =
      e?.response?.data?.message ||
      e?.message ||
      "Check-in failed";
  } finally {
    busy.value = false;
  }
}

async function confirmCheckIn(mode) {
  busy.value = true;
  error.value = null;

  try {
    const payload = await getLocationPayloadStrict();

    payload.work_mode = mode; // ✅ Field / Onsite

    await axios.post("employee/attendance/check-in", payload);

    selectingMode.value = false;
    selectedWorkMode.value = null;

    await fetchEmployeeAttendance();
  } catch (e) {
    error.value =
      e?.response?.data?.message ||
      e?.message ||
      "Check-in failed";
  } finally {
    busy.value = false;
  }
}

async function checkOut() {
  busy.value = true;
  error.value = null;
  try {
    const payload = await getLocationPayloadStrict();
    await axios.post("employee/attendance/check-out", payload);
    await fetchEmployeeAttendance();
  } catch (e) {
    error.value =
      e?.response?.data?.message ||
      e?.message ||
      "Check-out failed";
  } finally {
    busy.value = false;
  }
}


// NAV (✅ same route, query param)
function goMyAttendance() {
  router.push({ path: "/employee/attendance", query: { view: "me" } });
}
function goAdminAttendance() {
  router.push({ path: "/employee/attendance", query: {} });
}

// Charger selon mode
async function loadDataByMode() {
  grouped.value = {};
  error.value = null;
  if (isEmployeeMode.value) await fetchEmployeeAttendance();
  else await fetchAdminAttendance();
}

onMounted(loadDataByMode);

// ✅ si on change ?view=me <-> admin, on recharge
watch(() => route.query.view, () => {
  loadDataByMode();
});
</script>

<style scoped>
.text-burgundy { color: #b03a45; }
.bg-burgundy { background: linear-gradient(195deg, #4b5563, #8d2e38); }
.card { border-radius: 0.5rem; }
.alert { margin-bottom: 1rem; }

.btn-burgundy {
  background: #8d2e38;
  border: none;
  color: #fff;
}
.btn-burgundy:hover { background: #7a2630; }
</style>
